import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { getVessels } from "../../utils/ApiCalls/SpecialPage/index";
import {
  createEntertainmentRvc,
  updateEntertainmentRvc,
} from "../../utils/ApiCalls/BackOffice/Entertainment";
import { addlog } from "../../utils/ApiCalls/Logs";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";

const MANAGEMENT_PATH = "/shore/back-office/entertainment/management";

const FIELD_LABELS = {
  vesselId: "Vessel",
  name: "Name",
  nameSimplifiedChinese: "Name (SC)",
  nameTraditionalChinese: "Name (TC)",
};

// Builds "Vessel: Genting Dream -> World Dream, Name: Old -> New" from only
// the fields that actually changed
const buildFieldChanges = (oldValues, newValues, resolveValue = (v) => v) =>
  Object.keys(FIELD_LABELS)
    .filter((key) => String(oldValues[key]) !== String(newValues[key]))
    .map(
      (key) =>
        `${FIELD_LABELS[key]}: ${resolveValue(key, oldValues[key])} -> ${resolveValue(key, newValues[key])}`
    )
    .join(", ");

const EntertainmentRvcInput = ({ rvc, defaultVesselId, onSuccess, readOnly }) => {
  const [rvcId, setRvcId] = useState("");
  const [vesselId, setVesselId] = useState(defaultVesselId || "");
  const [name, setName] = useState("");
  const [nameSimplifiedChinese, setNameSimplifiedChinese] = useState("");
  const [nameTraditionalChinese, setNameTraditionalChinese] = useState("");
  const [vessels, setVessels] = useState([]);
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [error, setError] = useState();

  const navigate = useNavigate();
  const isEdit = !!rvc?.id;

  useEffect(() => {
    const loadVessels = async () => {
      const data = await getVessels();
      if (!data?.error) setVessels(data || []);
    };
    loadVessels();
  }, []);

  useEffect(() => {
    if (rvc?.id) {
      setRvcId(rvc.rvcId ?? "");
      setVesselId(rvc.vesselId ?? "");
      setName(rvc.name || "");
      setNameSimplifiedChinese(rvc.nameSimplifiedChinese || "");
      setNameTraditionalChinese(rvc.nameTraditionalChinese || "");
    }
  }, [rvc]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!vesselId || !name || (!isEdit && rvcId === "")) {
      const message = `Error: The following fields are empty - ${
        !isEdit && rvcId === "" ? "rvc id, " : ""
      }vessel, name`;
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message, type: "error" },
      });
      setError(message);
      return;
    }

    setError();

    const values = isEdit
      ? { vesselId, name, nameSimplifiedChinese, nameTraditionalChinese }
      : { rvcId, vesselId, name, nameSimplifiedChinese, nameTraditionalChinese };

    const data = isEdit
      ? await updateEntertainmentRvc(rvc.id, values)
      : await createEntertainmentRvc(values);

    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }

    dispatch({
      type: "SET_ERROR_MESSAGE",
      errMess: {
        message: isEdit ? "Revenue center updated" : "Revenue center created",
        type: "success",
      },
    });

    let action = LOG_ACTIONS.CREATED;
    if (isEdit) {
      const resolveValue = (key, val) =>
        key === "vesselId" ? vessels.find((v) => v.id === val)?.name || val : val;
      const changes = buildFieldChanges(
        {
          vesselId: rvc.vesselId,
          name: rvc.name,
          nameSimplifiedChinese: rvc.nameSimplifiedChinese,
          nameTraditionalChinese: rvc.nameTraditionalChinese,
        },
        values,
        resolveValue
      );
      action = changes ? `${LOG_ACTIONS.UPDATED} (${changes})` : LOG_ACTIONS.UPDATED;
    }

    addlog(
      action,
      LOG_TYPES.ENTERTAINMENT_RVC,
      { id: rvc?.id || data?.data?.id, title: name, category: ENTERTAINMENT_LOG_CATEGORIES.RVC },
      userDetails.id
    );

    if (onSuccess) {
      onSuccess(data?.data || data);
    } else {
      navigate(
        vesselId ? `${MANAGEMENT_PATH}?vesselId=${vesselId}` : MANAGEMENT_PATH
      );
    }
  };

  return (
    <div>
      <section className="content">
        {error && (
          <div className="d-flex justify-content-center align-items-center text-danger">
            <p>{error}</p>
          </div>
        )}

        <div className="container-fluid">
          <div className="card">
            <form className="input_form" onSubmit={submitHandler}>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="rvcId" className="f_label">RVC ID <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="rvcId"
                        required
                        disabled={isEdit || readOnly}
                        className="f_input"
                        type="text"
                        value={rvcId}
                        onChange={(e) => setRvcId(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="vesselId" className="f_label">Vessel <span style={{ color: "red" }}>*</span></label>
                      <Select
                        id="vesselId"
                        size="small"
                        fullWidth
                        displayEmpty
                        disabled={readOnly}
                        value={vesselId}
                        onChange={(e) => setVesselId(e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          Select vessel
                        </MenuItem>
                        {vessels.map((v) => (
                          <MenuItem key={v.id} value={v.id}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="name" className="f_label">Name <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="name"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="nameSimplifiedChinese" className="f_label">Name (Simplified Chinese)</label>
                      <input
                        id="nameSimplifiedChinese"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={nameSimplifiedChinese}
                        onChange={(e) => setNameSimplifiedChinese(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="nameTraditionalChinese" className="f_label">Name (Traditional Chinese)</label>
                      <input
                        id="nameTraditionalChinese"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={nameTraditionalChinese}
                        onChange={(e) => setNameTraditionalChinese(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!readOnly && (
                <div className="card-footer text-right">
                  <button className="btn main_submit">
                    {isEdit ? "Update" : "Add"} revenue center
                  </button>
                </div>
              )}
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default EntertainmentRvcInput;
